import { ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import BrandLogo from './BrandLogo';
import Footer from './Footer';
import { BRAND } from '../lib/brand';

interface LegalLayoutProps {
  title: string;
  /** ISO-style date shown under the H1, defaults to BRAND.lastUpdated */
  lastUpdated?: string;
  /** Optional short subtitle line under the date */
  intro?: string;
  children: ReactNode;
}

export default function LegalLayout({
  title,
  lastUpdated,
  intro,
  children,
}: LegalLayoutProps) {
  // Ensure dark mode (the rest of the app does this only on Home)
  useEffect(() => {
    document.documentElement.classList.add('dark');
    window.scrollTo(0, 0);
    return () => {
      document.documentElement.classList.remove('dark');
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col">
      {/* Slim brand-only nav */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/70 border-b border-border">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 md:px-8 h-16">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo className="h-5 w-5" />
            <span className="text-base font-semibold tracking-tight">
              {BRAND.name}
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to home</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 px-6 md:px-8 py-16 md:py-24">
        <article className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">
            {title}
          </h1>
          <p className="mt-3 text-sm uppercase tracking-[0.18em] text-muted-foreground">
            Last updated · {lastUpdated ?? BRAND.lastUpdated}
          </p>
          {intro && (
            <p className="mt-6 text-base md:text-lg text-foreground/75 leading-relaxed">
              {intro}
            </p>
          )}

          <div className="mt-12 space-y-12 text-foreground/80 leading-relaxed prose-legal">
            {children}
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}

/**
 * A section block — handles consistent H2 + body spacing.
 */
export function LegalSection({
  heading,
  children,
  id,
}: {
  heading: string;
  id?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-2xl md:text-[1.75rem] font-semibold tracking-tight text-foreground">
        {heading}
      </h2>
      <div className="mt-4 space-y-4 text-foreground/75">{children}</div>
    </section>
  );
}

/** Sub-heading inside a section */
export function LegalSubheading({ children }: { children: ReactNode }) {
  return (
    <h3 className="mt-6 text-lg font-semibold text-foreground">{children}</h3>
  );
}

/** Bullet list with the right vertical rhythm */
export function LegalList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="mt-3 space-y-2 list-disc pl-6 marker:text-foreground/40">
      {items.map((it, i) => (
        <li key={i} className="text-foreground/75 leading-relaxed">
          {it}
        </li>
      ))}
    </ul>
  );
}
